/**
 * ViewSettings type definitions
 *
 * Mirrors TL.Model.ViewSettings (web/TL/model.js). The legacy Backbone model
 * is the persistence layer (localStorage) during the migration; the Svelte
 * store is the runtime source of truth for the UI.
 */

import type { DeepPartial } from './utils';

export interface WorkordersFilter {
  'workorders-all': boolean;
  new: boolean;
  assigned: boolean;
  inWork: boolean;
  paused: boolean;
  complete: boolean;
  delayed: boolean;
}

export interface SitesFilter {
  'sites-all': boolean;
  addresses: boolean;
  areas: boolean;
  tours: boolean;
}

export interface ResourceIcons {
  positions: boolean;
  events: boolean;
}

export interface NotificationOptions {
  workorderNotifications: boolean;
  messageNotifications: boolean;
}

export interface MapOverlays {
  trafficLayer: boolean;
  osmOverlay: boolean;
  optmOverlay: boolean;
  showGooglePoi: boolean;
}

export type MapType = 'automatic' | 'Mapnik' | 'Google' | (string & {});

export interface MapSettings {
  overlays: MapOverlays;
  type: MapType;
}

export interface TripsSettings {
  chart0: string;
  chart1: string;
  showOverview: boolean;
}

export type SidebarStatus = 'iconView' | 'listView' | (string & {});

export interface ViewSettings {
  autocenter: boolean;
  resourceIcons: ResourceIcons;
  resourceLabels: boolean;
  resourceGrouping: boolean;
  filterPositions: boolean;
  filterViewport: boolean;
  workorders: WorkordersFilter;
  workorderLabels: boolean;
  workorderGrouping: boolean;
  workorderTraces: boolean;
  sites: SitesFilter;
  siteLabels: boolean;
  siteGrouping: boolean;
  heapTable: boolean;
  notifications: boolean;
  notificationOptions: NotificationOptions;
  sidebarStatus: SidebarStatus;
  map: MapSettings;
  trips: TripsSettings;
}

export type ViewSettingsPatch = DeepPartial<ViewSettings>;

/**
 * Default state — mirrors TL.Model.ViewSettings.defaults exactly.
 */
export const DEFAULT_VIEW_SETTINGS: ViewSettings = {
  autocenter: false,
  resourceIcons: {
    positions: false,
    events: true,
  },
  resourceLabels: true,
  resourceGrouping: false,
  filterPositions: false,
  filterViewport: false,
  workorders: {
    'workorders-all': false,
    new: false,
    assigned: false,
    inWork: false,
    paused: false,
    complete: false,
    delayed: false,
  },
  workorderLabels: false,
  workorderGrouping: false,
  workorderTraces: true,
  sites: {
    'sites-all': false,
    addresses: false,
    areas: false,
    tours: false,
  },
  siteLabels: false,
  siteGrouping: false,
  heapTable: true,
  notifications: true,
  notificationOptions: {
    workorderNotifications: true,
    messageNotifications: true,
  },
  sidebarStatus: 'iconView',
  map: {
    overlays: {
      trafficLayer: false,
      osmOverlay: false,
      optmOverlay: false,
      showGooglePoi: false,
    },
    type: 'automatic',
  },
  trips: {
    chart0: 'speed',
    chart1: 'none',
    showOverview: false,
  },
};
